import type {
  AdditionalInformationContinuationNote,
  AdditionalInformationLimits,
  PrintedDocumentRow,
} from './PrintedDocumentRow';
import { CitizenshipSelectionOptions } from '@/features/section-1/features/citizenship-selection/application/domain/entities/CitizenshipSelection';
import { Section2Form } from '../../validators/section-2.schema';
import { Section2Steps } from '../models/section-2-steps';
import { RemoteI9Status } from '@/shared/application/domain/entities/RemoteI9Status';

export type { DocumentList } from './DocumentType';

/** One labeled upload of a past version: a combination's other document or a supporting document. */
export interface LabeledUploadAsOf {
  list: 'A' | 'B' | 'C' | null;
  role: 'component' | 'supporting';
  label: string;
  front?: string;
  back?: string;
}
/**
 * Section 1 Summary Entity
 * Read-only data from Section 1 to display in Section 2
 */
/** The Section 1 correction that returned a signed Section 2 to the employer's review (VTK-2182). */
export interface Section1Correction {
  correctedAt: Date;
  reason: string | null;
}

export interface Section1Summary {
  i9FormId: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  otherName?: string;
  /** Null when a digitized paper I-9 had no readable date of birth. */
  dateOfBirth: Date | null;
  ssn: string;
  email?: string;
  phoneNumber?: string;
  address: string;
  city: string;
  /** Null when a digitized paper I-9 had no readable state. */
  state: string | null;
  zipCode: string;
  citizenshipStatus: CitizenshipSelectionOptions;
  apartmentNumber?: string;
}

/**
 * Section 2 Domain Entity
 * Contains form fields that can be directly used to reset form data
 * Follows Section 1 pattern with formFields: Partial<Form>
 */
export class Section2 {
  formFields: Partial<Section2Form>;
  i9FormId: string;
  currentStep: Section2Steps;
  eVerifyCaseQueueId: string | null;
  businessName?: string;
  businessAddress?: string;
  /**
   * The persisted Section 2 signer name (written at signature time). Unlike
   * `formFields.signerFullName`, this is NOT gated on `isAuthorisedUser` form state, so it is
   * the field the rendered Form I-9 must display (VTK-1942).
   */
  signerName?: string;
  /** Title of the employer or authorized representative, printed after the name. */
  signerTitle?: string | null;
  savedAt?: string;
  status?: string;
  isRemoteI9?: boolean; // Flag to indicate if this is a remote I-9 case
  remoteI9Status?: RemoteI9Status;
  remoteI9Type?: RemoteI9Type;
  /** The employer supplied the first day of employment on the invite — VTK-1953. */
  hireDateFromEmployer?: boolean;
  /** The Additional Information notes the documents call for, for the completer to review (VTK-2038). */
  suggestedAdditionalInformation?: string[];
  /** The rows the printed I-9 shows for a List A combination, or null for its single row. */
  printedListARows?: PrintedDocumentRow[] | null;
  /** A receipt in List B or C prints its own row: "Receipt: " and the document it replaces. */
  printedListBRows?: PrintedDocumentRow[] | null;
  printedListCRows?: PrintedDocumentRow[] | null;
  /** The printed Additional Information box's size, as the server enforces it. */
  additionalInformationLimits?: AdditionalInformationLimits;
  /**
   * Notes the printed box could not hold, printed on an "Additional Information (continued)" page
   * after Section 2 (VTK-2099). The box itself is never altered for them.
   */
  additionalInformationContinued?: AdditionalInformationContinuationNote[];
  /**
   * The labeled uploads as they stood at a past version (an amendment's snapshot). When set, the
   * record's document pages show these instead of reading today's (VTK-2145).
   */
  labeledUploadsAsOf?: LabeledUploadAsOf[];
  /**
   * Whether the Section 2 signer used the DHS-authorized alternative procedure; null when never asked.
   * The rendered I-9 checks its box only when this is true (VTK-1956).
   */
  alternativeProcedureUsed?: boolean | null;
  /** An in-person Section 2 back in the employer's review after a Section 1 correction (VTK-2182). */
  awaitingInPersonReview?: boolean;
  section1Correction?: Section1Correction | null;

  constructor(
    formFields: Partial<Section2Form>,
    i9FormId: string,
    currentStep: Section2Steps,
    eVerifyCaseQueueId: string | null,
    businessName?: string,
    businessAddress?: string,
    savedAt?: string,
    status?: string
  ) {
    this.formFields = formFields;
    this.i9FormId = i9FormId;
    this.currentStep = currentStep;
    this.eVerifyCaseQueueId = eVerifyCaseQueueId;
    this.businessName = businessName;
    this.businessAddress = businessAddress;
    this.savedAt = savedAt;
    this.status = status;
  }
}

/**
 * Dynamic Field Types — re-exported from shared
 */
export type {
  DynamicFieldType,
  ConditionOperator,
  ValidationCondition,
  CompoundCondition,
  WhenClause,
  ConditionalValidationRule,
  ValidationRule,
  DateValidationObject,
  DynamicFieldValidation,
  DynamicFieldOption,
  DynamicFieldSchema,
} from '@/shared/application/domain/entities/DynamicField';

import type { DynamicFieldSchema } from '@/shared/application/domain/entities/DynamicField';
import { RemoteI9Type } from '@/shared/application/domain/entities/RemoteI9Type';

export interface DocumentMetadataSchemaResponse {
  fields: DynamicFieldSchema[];
}

export default Section2;
